"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { LandingNav } from "@/app/landing-nav";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import { ExamResourceSidebar, RESOURCE_LINKS, MAIN_ONLY_RESOURCE_PATHS } from "./exam-resource-sidebar";

const MAIN_EXAMS: readonly string[] = ["toefl", "sat", "act", "ielts", "gre"];
import { track } from "@/lib/analytics";

/**
 * ExamResourceLayout — the shell for every exam resource sub-page
 * (/toefl/guide, /sat/scoring-guide, /ielts/question-types, etc.).
 *
 * Wraps:
 *   LandingNav
 *   mobile nav strip  (breadcrumb + hamburger drawer trigger)   — only <lg
 *   ExamResourceSidebar (persistent left rail)                   — only lg+
 *   main content                                                  — children
 *   prev/next footer nav (automatic, based on RESOURCE_LINKS)
 *
 * Pages pass optional `tocItems` to render an "On this page" list
 * below the resource nav in the sidebar (and in the mobile drawer).
 */

const COLOR_CLASSES: Record<string, { text: string; bgHover: string; border: string; ring: string }> = {
  blue:   { text: "text-blue-700",   bgHover: "hover:bg-blue-50",   border: "border-blue-100",   ring: "focus:ring-blue-500"   },
  green:  { text: "text-green-700",  bgHover: "hover:bg-green-50",  border: "border-green-100",  ring: "focus:ring-green-500"  },
  purple: { text: "text-purple-700", bgHover: "hover:bg-purple-50", border: "border-purple-100", ring: "focus:ring-purple-500" },
  indigo: { text: "text-indigo-700", bgHover: "hover:bg-indigo-50", border: "border-indigo-100", ring: "focus:ring-indigo-500" },
  orange: { text: "text-orange-700", bgHover: "hover:bg-orange-50", border: "border-orange-100", ring: "focus:ring-orange-500" },
};

function getColors(color: string) {
  return COLOR_CLASSES[color] ?? COLOR_CLASSES.blue;
}

interface ExamResourceLayoutProps {
  examType: ExamType;
  /** Label for the current page, shown in breadcrumb (e.g. "Scoring Guide") */
  currentLabel: string;
  /** Optional TOC rendered in the sidebar */
  tocItems?: ReadonlyArray<readonly [string, string]>;
  children: React.ReactNode;
}

export function ExamResourceLayout({
  examType,
  currentLabel,
  tocItems,
  children,
}: ExamResourceLayoutProps) {
  const pathname = usePathname();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const config = EXAM_CONFIGS[examType];
  const colors = getColors(config.color);
  const base = `/${examType}`;

  // Close drawer on route change + track page view
  useEffect(() => {
    setDrawerOpen(false);
    // Track study material page views for admin analytics
    if (pathname) {
      track("page_view", { page: pathname, exam_type: examType });
    }
  }, [pathname, examType]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  // Prev / next siblings for the footer nav. Generic exams have no
  // study-plan / test-day / mistakes routes, so exclude them there or the
  // "next" link from /faq lands on a 404 (2026-09-13 crawl).
  const navLinks = MAIN_EXAMS.includes(examType)
    ? RESOURCE_LINKS
    : RESOURCE_LINKS.filter(link => !MAIN_ONLY_RESOURCE_PATHS.has(link.path));
  const currentIdx = navLinks.findIndex(
    link => pathname === `${base}${link.path}`,
  );
  const prevLink = currentIdx > 0 ? navLinks[currentIdx - 1] : null;
  const nextLink =
    currentIdx >= 0 && currentIdx < navLinks.length - 1
      ? navLinks[currentIdx + 1]
      : null;

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      {/* Mobile nav strip: breadcrumb on left, menu trigger on right.
          Sticky so it stays visible below LandingNav while scrolling. */}
      <div className="lg:hidden sticky top-[61px] z-20 border-b border-gray-100 bg-white/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center justify-between gap-2 text-sm">
          <Link
            href={base}
            className={`inline-flex items-center gap-1.5 font-medium min-w-0 ${colors.text} hover:opacity-70 transition-opacity`}
          >
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <config.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
            <span className="truncate">{config.name}</span>
          </Link>
          <span className="text-gray-300 mx-1 flex-shrink-0">/</span>
          <span className="text-gray-500 truncate flex-1">{currentLabel}</span>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open resource menu"
            className="flex-shrink-0 ml-2 p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile drawer overlay + panel */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          {/* Panel */}
          <div className="relative ml-auto w-72 max-w-[85%] h-full bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="text-sm font-semibold text-gray-900">
                {config.name} Resources
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="px-4 py-4">
              <ExamResourceSidebar
                examType={examType}
                tocItems={tocItems}
                onNavigate={() => setDrawerOpen(false)}
                inDrawer
              />
            </div>
          </div>
        </div>
      )}

      {/* Main two-column layout */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-10">
          <ExamResourceSidebar examType={examType} tocItems={tocItems} />

          <main className="flex-1 min-w-0">
            {children}

            {/* Prev / next footer nav */}
            {(prevLink || nextLink) && (
              <nav className="mt-16 border-t border-gray-100 pt-6 grid grid-cols-2 gap-4">
                <div>
                  {prevLink && (
                    <Link
                      href={`${base}${prevLink.path}`}
                      className={`group flex items-start gap-3 p-4 rounded-xl border border-gray-100 ${colors.bgHover} transition-colors`}
                    >
                      <svg
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400 group-hover:${colors.text}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      <div className="min-w-0">
                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                          Previous
                        </div>
                        <div className={`flex items-center gap-1.5 text-sm font-semibold text-gray-900 group-hover:${colors.text}`}>
                          <prevLink.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                          <span className="min-w-0 truncate">{prevLink.label}</span>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
                <div>
                  {nextLink && (
                    <Link
                      href={`${base}${nextLink.path}`}
                      className={`group flex items-start gap-3 p-4 rounded-xl border border-gray-100 ${colors.bgHover} transition-colors text-right`}
                    >
                      <div className="min-w-0 ml-auto">
                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                          Next
                        </div>
                        <div className={`flex items-center justify-end gap-1.5 text-sm font-semibold text-gray-900 group-hover:${colors.text}`}>
                          <nextLink.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                          <span className="min-w-0 truncate">{nextLink.label}</span>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400 group-hover:${colors.text}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  )}
                </div>
              </nav>
            )}
          </main>
        </div>
      </div>

      {/* Shared footer — same across every resource page for a given exam */}
      <footer className="border-t border-gray-100 py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© {new Date().getFullYear()} FullPracticeTests</span>
          <div className="flex items-center gap-5 flex-wrap justify-center">
            <Link href={base} className="hover:text-gray-600">
              Practice Exams
            </Link>
            {navLinks.map(link => (
              <Link
                key={link.path}
                href={`${base}${link.path}`}
                className="hover:text-gray-600"
              >
                {link.label}
              </Link>
            ))}
            <Link href="/pricing" className="hover:text-gray-600">
              Pricing
            </Link>
            <Link href="/privacy" className="hover:text-gray-600">
              Privacy
            </Link>
            <Link href="/terms" className="hover:text-gray-600">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
